'use client';

import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icons,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import type {
  ComponentVisibility,
  FormModule,
  ValidationRules,
} from '@commenda/types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ENTRY_ID_KEY } from '../../dsl-constants';
import { FormContext, type FormContextType } from '../../hooks/FormContext';

function makeInstanceId(): string {
  return `inst-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}


/** Error key for "this entry is still referenced", scoped to the instance so it clears with it. */
const REMOVE_BLOCKED_SUFFIX = '__removeBlocked__';

/**
 * Fields elsewhere in the form that still point at `entryId`. A checkbox group keeps a
 * map of option -> checked; a single-value field holds the id directly.
 */
function findFieldsReferencingEntry(
  formData: Record<string, unknown>,
  entryId: string,
  skipKey: string
): string[] {
  return Object.entries(formData).reduce<string[]>((acc, [fieldId, value]) => {
    if (fieldId === skipKey) return acc;
    if (value === entryId) acc.push(fieldId);
    else if (Array.isArray(value) && value.includes(entryId)) acc.push(fieldId);
    else if (
      value &&
      typeof value === 'object' &&
      (value as Record<string, unknown>)[entryId] === true
    ) {
      acc.push(fieldId);
    }
    return acc;
  }, []);
}

function RepeatableModuleInstance({
  module,
  instanceIndex,
  instanceData,
  saveKey,
  min,
  totalInstances,
  onRemove,
  removeBlockedMessage,
  componentRegistry,
  parentContext,
  isFieldVisible,
}: {
  module: FormModule;
  instanceIndex: number;
  instanceData: Record<string, unknown>;
  saveKey: string;
  min: number;
  totalInstances: number;
  onRemove: () => void;
  removeBlockedMessage?: string;
  componentRegistry: Record<string, React.FC<unknown>>;
  parentContext: FormContextType;
  isFieldVisible: (visibility?: ComponentVisibility) => boolean;
}) {
  const prefix = `${saveKey}.${instanceIndex}.`;
  const { formData, setFormData, errors, setErrors, validateField } =
    parentContext;
  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  const scopedErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(errors)
          .filter(([k]) => k.startsWith(prefix))
          .map(([k, v]) => [k.slice(prefix.length), v])
      ),
    [errors, prefix]
  );

  const updateFieldForInstance = useCallback(
    (fieldName: string, value: unknown) => {
      setFormData((prev: Record<string, unknown>) => {
        const arr = [
          ...(Array.isArray(prev[saveKey])
            ? (prev[saveKey] as Record<string, unknown>[])
            : []),
        ];
        while (arr.length <= instanceIndex) arr.push({});
        arr[instanceIndex] = {
          ...arr[instanceIndex],
          [fieldName]: value,
        };
        return { ...prev, [saveKey]: arr };
      });
    },
    [saveKey, instanceIndex, setFormData]
  );

  const scopedSetErrors = useCallback(
    (newErrors: Record<string, string>) => {
      const merged = { ...errorsRef.current };
      Object.keys(merged)
        .filter((k) => k.startsWith(prefix))
        .forEach((k) => delete merged[k]);
      Object.entries(newErrors).forEach(([k, v]) => {
        merged[`${prefix}${k}`] = v;
      });
      setErrors(merged);
    },
    [prefix, setErrors]
  );

  const scopedValidateField = useCallback(
    (fieldName: string, value: unknown, rules?: ValidationRules) => {
      // Unique values within repeatable module - check before other rules
      // Use `value` for current instance (real-time) since formData may not have updated yet
      if (rules?.uniqueInRepeatable?.enabled) {
        const instances = formData[saveKey] as
          | Record<string, unknown>[]
          | undefined;
        if (Array.isArray(instances)) {
          const values = instances.map((inst, i) =>
            i === instanceIndex ? value : ((inst ?? {})[fieldName] as unknown)
          );
          const nonEmpty = values.filter(
            (v) => v !== '' && v !== null && v !== undefined
          );
          const normalized = nonEmpty.map((v) => String(v));
          const currentNorm =
            value !== '' && value !== null && value !== undefined
              ? String(value)
              : null;
          if (
            currentNorm &&
            normalized.filter((v) => v === currentNorm).length > 1
          ) {
            const message =
              rules.uniqueInRepeatable.message ||
              'This value must be unique across all entries';
            scopedSetErrors({ [fieldName]: message });
            return message;
          }
        }
      }

      const prefixedRules =
        rules?.notSameAs?.field && !rules.notSameAs.field.includes('.')
          ? {
              ...rules,
              notSameAs: {
                ...rules.notSameAs,
                field: `${prefix}${rules.notSameAs.field}`,
              },
            }
          : rules;
      return validateField(`${prefix}${fieldName}`, value, prefixedRules);
    },
    [formData, instanceIndex, prefix, saveKey, scopedSetErrors, validateField]
  );

  // Scope isFieldVisible to read from instanceData instead of parent formData.
  // Visibility conditions inside a repeatable module reference fields within
  // the same instance (e.g. a checkbox controlling a sibling). The parent's
  // isFieldVisible would look for these at the top level where they don't
  // exist. If the field isn't found in instanceData, fall back to the parent's
  // isFieldVisible for cross-module conditions.
  const scopedIsFieldVisible = useCallback(
    (visibility?: ComponentVisibility): boolean => {
      if (!visibility) return true;

      const { condition } = visibility;

      if (
        condition.field &&
        condition.operator &&
        (condition.value !== undefined || condition.operator === 'isNotEmpty')
      ) {
        const data = instanceData ?? {};
        const fieldValue = data[condition.field];

        // If the field exists in this instance's data, evaluate here
        if (fieldValue !== undefined) {
          const fieldComponent = parentContext.componentMap?.[
            condition.field
          ] as { component?: string } | undefined;

          // Handle CheckboxGroup fields
          if (
            fieldComponent?.component === 'CheckboxGroup' &&
            typeof fieldValue === 'object' &&
            fieldValue !== null &&
            !Array.isArray(fieldValue)
          ) {
            const checkboxValue = fieldValue as Record<string, boolean>;
            const optionKey = String(condition.value);

            switch (condition.operator) {
              case 'equals':
                return checkboxValue[optionKey] === true;
              case 'notEquals':
                return checkboxValue[optionKey] !== true;
              case 'contains':
                return Object.entries(checkboxValue).some(
                  ([key, checked]) => checked === true && key === optionKey
                );
              case 'isNotEmpty':
                return Object.values(checkboxValue).some(
                  (checked) => checked === true
                );
              default:
                return true;
            }
          }

          // Default comparison
          switch (condition.operator) {
            case 'equals':
              return fieldValue === condition.value;
            case 'notEquals':
              return fieldValue !== condition.value;
            case 'greaterThan':
              return Number(fieldValue) > Number(condition.value);
            case 'lessThan':
              return Number(fieldValue) < Number(condition.value);
            case 'contains':
              if (Array.isArray(fieldValue)) {
                return fieldValue.some((item) => item === condition.value);
              }
              return String(fieldValue ?? '').includes(String(condition.value));
            case 'isNotEmpty':
              return Array.isArray(fieldValue)
                ? fieldValue.length > 0
                : fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
            default:
              return true;
          }
        }
      }

      // Fall back to parent's isFieldVisible for cross-module conditions
      // or compound (and/or) conditions
      return isFieldVisible(visibility);
    },
    [instanceData, parentContext.componentMap, isFieldVisible]
  );

  const scopedContext: FormContextType = useMemo(
    () => ({
      ...parentContext,
      formData: instanceData ?? {},
      updateField: updateFieldForInstance,
      errors: scopedErrors,
      setErrors: scopedSetErrors,
      validateField: scopedValidateField,
      isFieldVisible: scopedIsFieldVisible,
    }),
    [
      parentContext,
      instanceData,
      updateFieldForInstance,
      scopedErrors,
      scopedSetErrors,
      scopedValidateField,
      scopedIsFieldVisible,
    ]
  );

  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <FormContext.Provider value={scopedContext}>
      <Collapsible
        open={isExpanded}
        onOpenChange={setIsExpanded}
        className="rounded-lg border border-border-secondary bg-bg-primary p-4"
      >
        {/* Remove sits outside the trigger — collapsing a row and deleting it are
            different intentions and must not share a hit area. */}
        <div className="flex items-center justify-between mb-4">
          <CollapsibleTrigger className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer">
            <Icons.ChevronDown
              className={cn(
                'w-4 h-4 text-text-tertiary transition-transform shrink-0',
                !isExpanded && '-rotate-90'
              )}
            />
            <span className="txt-small-medium text-text-secondary truncate">
              {module.name}
              {totalInstances > 1 ? ` ${instanceIndex + 1}` : ''}
            </span>
          </CollapsibleTrigger>
          {totalInstances > min && (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={onRemove}
            >
              <Icons.Trash01 className="w-4 h-4" />
            </Button>
          )}
        </div>
        {removeBlockedMessage && (
          <p className="txt-small-regular text-text-error mb-3">
            {removeBlockedMessage}
          </p>
        )}
        <CollapsibleContent className="space-y-3">
          {module.components
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((component, compIndex) => {
              if (!scopedIsFieldVisible(component.visibility)) return null;
              const Component = componentRegistry[component.component];
              if (!Component) return null;
              const Comp = Component as React.FC<Record<string, unknown>>;
              return (
                <div
                  key={
                    component.id ??
                    component.fieldId ??
                    `${component.component}-${instanceIndex}-${compIndex}`
                  }
                  data-field-id={
                    component.fieldId
                      ? `${saveKey}.${instanceIndex}.${component.fieldId}`
                      : undefined
                  }
                >
                  <Comp {...component} />
                </div>
              );
            })}
        </CollapsibleContent>
      </Collapsible>
    </FormContext.Provider>
  );
}

export const RepeatableModuleRenderer = ({
  module,
  componentRegistry,
  parentContext,
  isFieldVisible,
}: {
  module: FormModule;
  componentRegistry: Record<string, React.FC<unknown>>;
  parentContext: FormContextType;
  isFieldVisible: (visibility?: ComponentVisibility) => boolean;
}) => {
  const { repeatable } = module;
  const saveKey = module.saveKey || module.id;
  const { formData, setFormData, errors, setErrors, validateField, componentMap } =
    parentContext;

  const rawMin = repeatable?.minFromField
    ? Number(parentContext.formData[repeatable.minFromField]) ||
      (repeatable?.min ?? 1)
    : (repeatable?.min ?? 1);
  const min = Math.max(1, rawMin);
  const rawMax = repeatable?.maxFromField
    ? Number(parentContext.formData[repeatable.maxFromField]) ||
      (repeatable?.max ?? 10)
    : (repeatable?.max ?? 10);
  const max = Math.max(min, rawMax);

  const addButtonLabel = repeatable?.addButtonLabel ?? 'Add Another';
  const addButtonVariant =
    (repeatable?.addButtonVariant as
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | 'link'
      | undefined) ?? 'secondary';
  const addButtonSize =
    (repeatable?.addButtonSize as 'sm' | 'md' | 'lg' | 'xl' | undefined) ??
    'sm';
  // Compact by default, matching the NestedForm add control; a DSL can still
  // opt into the full-width block button.
  const addButtonFullWidth = repeatable?.addButtonFullWidth ?? false;
  const addButtonAlignment =
    (repeatable?.addButtonAlignment as
      | 'left'
      | 'center'
      | 'right'
      | undefined) ?? 'left';

  const currentArray = formData[saveKey] as
    | Record<string, unknown>[]
    | undefined;
  const instancesArray = Array.isArray(currentArray) ? currentArray : [];
  const instanceCount = Math.max(
    min,
    Math.min(max, Math.max(instancesArray.length, min))
  );

  const effectiveInstances = useMemo(() => {
    const arr = [...instancesArray];
    while (arr.length < instanceCount) {
      arr.push({});
    }
    return arr.slice(0, instanceCount);
  }, [instancesArray, instanceCount]);

  // Entries carry their own id. It keys the row (an index key remounts the
  // surviving instance at N with the next one's data after a removal), and it
  // is what other fields reference when they offer these entries as options.
  // Entries saved before ids existed, and the blank ones padded in above, are
  // backfilled here rather than at render so the write happens once.
  useEffect(() => {
    if (effectiveInstances.every((entry) => typeof entry?.[ENTRY_ID_KEY] === 'string')) {
      return;
    }
    setFormData((prev) => {
      const current = Array.isArray(prev[saveKey])
        ? (prev[saveKey] as Record<string, unknown>[])
        : [];
      const padded = [...current];
      while (padded.length < effectiveInstances.length) padded.push({});
      return {
        ...prev,
        [saveKey]: padded.map((entry) =>
          typeof entry?.[ENTRY_ID_KEY] === 'string'
            ? entry
            : { ...(entry ?? {}), [ENTRY_ID_KEY]: makeInstanceId() }
        ),
      };
    });
  }, [effectiveInstances, saveKey, setFormData]);

  // Re-validate uniqueInRepeatable fields across instances when formData
  // changes — real-time, so that typing a duplicate in one row shows / clears
  // the error on the other matching rows.
  useEffect(() => {
    if (!Array.isArray(instancesArray)) return;
    module.components?.forEach((component) => {
      if (
        !component.fieldId ||
        !component.validation?.rules?.uniqueInRepeatable?.enabled
      ) {
        return;
      }
      instancesArray.forEach((inst, idx) => {
        const fieldId = `${saveKey}.${idx}.${component.fieldId}`;
        const value = (inst ?? {})[component.fieldId];
        validateField(fieldId, value, component.validation?.rules);
      });
    });
  }, [instancesArray, module.components, saveKey, validateField]);

  // Drop a "still selected" message once the reference it named is gone, so the row
  // does not keep warning about something the user has already cleared.
  useEffect(() => {
    const stale = Object.keys(errors).filter((key) => {
      if (!key.startsWith(`${saveKey}.`) || !key.endsWith(REMOVE_BLOCKED_SUFFIX)) return false;
      const index = Number(key.slice(saveKey.length + 1, key.lastIndexOf('.')));
      const entryId = effectiveInstances[index]?.[ENTRY_ID_KEY];
      if (typeof entryId !== 'string') return true;
      return findFieldsReferencingEntry(formData, entryId, saveKey).length === 0;
    });
    if (stale.length === 0) return;
    const next = { ...errors };
    stale.forEach((key) => delete next[key]);
    setErrors(next);
  }, [errors, formData, effectiveInstances, saveKey, setErrors]);

  const addInstance = useCallback(() => {
    setFormData((prev) => {
      const current = Array.isArray(prev[saveKey])
        ? (prev[saveKey] as Record<string, unknown>[])
        : [];
      const padded = [...current];
      while (padded.length < min) padded.push({});
      return {
        ...prev,
        [saveKey]: [...padded, { [ENTRY_ID_KEY]: makeInstanceId() }],
      };
    });
  }, [saveKey, setFormData, min]);

  const removeInstance = useCallback(
    (index: number) => {
      // Removing an entry another field still points at would leave that field holding a
      // dangling id, so the reference has to be cleared where it was made.
      const entryId = effectiveInstances[index]?.[ENTRY_ID_KEY];
      if (typeof entryId === 'string') {
        const referencedBy = findFieldsReferencingEntry(formData, entryId, saveKey);
        if (referencedBy.length > 0) {
          const names = referencedBy
            .map((fieldId) => componentMap?.[fieldId]?.label || fieldId)
            .join(', ');
          setErrors({
            ...errors,
            [`${saveKey}.${index}.${REMOVE_BLOCKED_SUFFIX}`]: `Still selected in ${names} — clear it there before removing this entry.`,
          });
          return;
        }
      }

      setFormData((prev) => {
        const current = Array.isArray(prev[saveKey])
          ? (prev[saveKey] as Record<string, unknown>[])
          : [];
        const padded = [...current];
        while (padded.length < min) padded.push({});
        return {
          ...prev,
          [saveKey]: padded.filter((_, i) => i !== index),
        };
      });
      // Shift errors for instances after the removed one down by one so they
      // stay attached to the right instance; drop errors for the removed one.
      const removedPrefix = `${saveKey}.${index}.`;
      const instancePrefixPattern = new RegExp(
        `^${saveKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.(\\d+)\\.(.+)$`
      );
      const next = Object.entries(errors).reduce<Record<string, string>>(
        (acc, [k, v]) => {
          if (k.startsWith(removedPrefix)) return acc;
          const match = k.match(instancePrefixPattern);
          if (match) {
            const i = Number(match[1]);
            const rest = match[2];
            if (i > index) {
              acc[`${saveKey}.${i - 1}.${rest}`] = v;
              return acc;
            }
          }
          acc[k] = v;
          return acc;
        },
        {}
      );
      setErrors(next);
    },
    [errors, saveKey, setFormData, setErrors, min, effectiveInstances, formData, componentMap]
  );

  if (!module?.components?.length) {
    return (
      <div className="text-center py-8">
        <p className="txt-small-regular text-text-tertiary">
          This module has no components configured.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {effectiveInstances.map((instanceData, instanceIndex) => (
        <RepeatableModuleInstance
          key={
            (instanceData?.[ENTRY_ID_KEY] as string | undefined) ??
            `inst-fallback-${instanceIndex}`
          }
          module={module}
          instanceIndex={instanceIndex}
          instanceData={instanceData ?? {}}
          saveKey={saveKey}
          min={min}
          totalInstances={effectiveInstances.length}
          onRemove={() => removeInstance(instanceIndex)}
          removeBlockedMessage={
            errors[`${saveKey}.${instanceIndex}.${REMOVE_BLOCKED_SUFFIX}`]
          }
          componentRegistry={componentRegistry}
          parentContext={parentContext}
          isFieldVisible={isFieldVisible}
        />
      ))}
      {instanceCount < max && (
        <div
          className={
            addButtonFullWidth
              ? 'w-full'
              : `flex ${
                  addButtonAlignment === 'center'
                    ? 'justify-center'
                    : addButtonAlignment === 'right'
                      ? 'justify-end'
                      : 'justify-start'
                }`
          }
        >
          <Button
            type="button"
            variant={addButtonVariant}
            size={addButtonSize}
            fullWidth={addButtonFullWidth}
            onClick={addInstance}
          >
            <Icons.Plus className="w-4 h-4" />
            {addButtonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
